import { useEffect, useState } from "react";
import { http, formatApiError, askDelete, delTitle } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { RgpdEraseButton } from "../components/RgpdEraseButton";
import { RgpdExportButton } from "../components/RgpdExportButton";
import { UserPlus, UserCheck, Trash2, Mail, Phone, MapPin, Award, Eye, Ban, Cake, FileText, User, Undo2, RotateCcw, Inbox, ShieldAlert, History } from "lucide-react";

export const ageOf = (bd) => {
  if (!bd) return null;
  const b = new Date(bd), t = new Date();
  let a = t.getFullYear() - b.getFullYear();
  if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--;
  return isNaN(a) ? null : a;
};
const tabOf = (c) => (c.deleted ? "deleted" : c.blacklist_match ? "blacklisted" : (c.converted_to_agent_id || c.status === "hired") ? "hired" : "active");
const TABS = [
  ["active", "En cours", Inbox, "border-sky-600 text-sky-700"],
  ["hired", "Recrutés", UserCheck, "border-emerald-600 text-emerald-700"],
  ["blacklisted", "Blacklistés", Ban, "border-rose-600 text-rose-700"],
  ["deleted", "Supprimés", Trash2, "border-slate-600 text-slate-700"],
];

function useFileBlob(fid) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!fid) return;
    let u;
    http.get(`/files/${fid}/download`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [fid]);
  return url;
}

function CandidatePhoto({ fid, className, testid }) {
  const url = useFileBlob(fid);
  return (
    <div className={`shrink-0 rounded-md overflow-hidden bg-slate-100 grid place-items-center border border-slate-200 ${className}`} data-testid={testid}>
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : <User className="h-1/2 w-1/2 text-slate-300" />}
    </div>
  );
}

function CvButton({ fid }) {
  const [busy, setBusy] = useState(false);
  if (!fid) return <span className="text-xs text-slate-400 italic" data-testid="candidate-no-cv">Aucun CV joint</span>;
  const open = async () => {
    const win = window.open("", "_blank");
    setBusy(true);
    try {
      const r = await http.get(`/files/${fid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      if (win) win.location.href = url; else window.open(url, "_blank");
    } catch { if (win) win.close(); } finally { setBusy(false); }
  };
  return (
    <button type="button" data-testid="candidate-open-cv" onClick={open} disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-sky-300 text-sky-800 bg-sky-50 hover:bg-sky-100 disabled:opacity-60">
      <FileText className="h-3.5 w-3.5" /> {busy ? "Ouverture…" : "Consulter le CV"}
    </button>
  );
}

function BlacklistBadge({ m }) {
  if (!m) return null;
  return <span title={`${m.name} — ${m.reason}`} className="inline-flex items-center gap-1 rounded bg-rose-600 text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5"><Ban className="h-2.5 w-2.5" />Blacklisté</span>;
}

const STATUS_LABELS = {
  new: { label: "Nouveau", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  reviewed: { label: "Examiné", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  hired: { label: "Recruté", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  rejected: { label: "Refusé", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

function DiplomaBadge({ label, info }) {
  if (!info?.has) return <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 border border-slate-200">{label} : Non</span>;
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
      {label} : Oui{info.expires_on ? ` · exp. ${info.expires_on}` : ""}
    </span>
  );
}

function CandidateDialog({ candidate, onClose, onConverted, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const convert = async () => {
    if (!window.confirm(`Convertir ${candidate.first_name} ${candidate.last_name} en fiche agent ?`)) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      const r = await http.post(`/candidates/${candidate.id}/convert-to-agent`);
      setMsg(`Agent créé (id: ${r.data.agent_id}).`);
      onConverted?.();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const run = async (confirmMsg, fn, isDel = false) => {
    if (confirmMsg && !(isDel ? askDelete(confirmMsg) : window.confirm(confirmMsg))) return;
    setBusy(true); setErr("");
    try { await fn(); onDeleted?.(); onClose(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const del = () => run("Mettre cette candidature dans « Supprimés » ? Vous pourrez la restaurer.", () => http.delete(`/candidates/${candidate.id}`), true);
  const delForever = () => run("Supprimer DÉFINITIVEMENT cette candidature, sa photo et son CV ? Cette action est irréversible.", () => http.delete(`/candidates/${candidate.id}?permanent=true`), true);
  const restore = () => run(null, () => http.post(`/candidates/${candidate.id}/restore`));
  const cancelHire = () => run(`Annuler le recrutement de ${candidate.first_name} ${candidate.last_name} ?\n\nLa fiche agent créée sera SUPPRIMÉE (ses vacations seront libérées) et le candidat repassera en « Examiné » dans les candidatures en cours.`, () => http.post(`/candidates/${candidate.id}/cancel-hire`));

  const setStatus = async (s) => {
    setBusy(true);
    try { await http.patch(`/candidates/${candidate.id}/status`, { status: s }); onConverted?.(); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4">
      <div className="bg-white rounded-md w-full max-w-2xl border border-slate-200 max-h-[90vh] overflow-auto" onClick={(e)=>e.stopPropagation()} data-testid="candidate-dialog">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold">{candidate.civility} {candidate.first_name} {candidate.last_name}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded text-slate-500">✕</button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          {candidate.blacklist_match && (
            <div data-testid="candidate-blacklist-banner" className="rounded-md border border-rose-300 bg-rose-50 px-4 py-3 text-rose-900">
              <div className="font-bold flex items-center gap-1.5"><Ban className="h-4 w-4" /> Personne blacklistée — recrutement interdit</div>
              <div className="text-xs mt-1">Correspond à l'agent <b>{candidate.blacklist_match.name}</b> ({candidate.blacklist_match.matched_on.join(", ")}).</div>
              <div className="text-xs mt-0.5">Motif : {candidate.blacklist_match.reason}</div>
            </div>
          )}
          <div className="flex gap-4 items-start">
            <CandidatePhoto fid={candidate.photo_file_id} className="h-36 w-28" testid="candidate-photo" />
            <div className="flex-1 space-y-2 pt-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Pièces jointes</div>
              <CvButton fid={candidate.cv_file_id} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" /> <a className="text-sky-700 hover:underline" href={`mailto:${candidate.email}`}>{candidate.email}</a></div>
            <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" /> <a className="text-slate-700" href={`tel:${candidate.phone}`}>{candidate.phone}</a></div>
            {candidate.address && (
              <div className="md:col-span-2 flex items-start gap-2 text-slate-700"><MapPin className="h-4 w-4 text-slate-400 mt-0.5" /> <span>{candidate.address}{candidate.postal_code ? `, ${candidate.postal_code}` : ""} {candidate.city || ""}</span></div>
            )}
            <div className="flex items-center gap-2" data-testid="candidate-age"><Cake className="h-4 w-4 text-slate-400" /> {candidate.birth_date ? <>Né(e) le {new Date(candidate.birth_date).toLocaleDateString("fr-FR")} · <b>{ageOf(candidate.birth_date)} ans</b></> : <span className="text-slate-400 italic">Date de naissance non renseignée</span>}</div>
            <div className="md:col-span-2 flex items-center gap-2"><Award className="h-4 w-4 text-slate-400" /> <span>Carte pro : <b>{candidate.carte_pro_number}</b></span></div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Diplômes</div>
            <div className="flex flex-wrap gap-1.5">
              <DiplomaBadge label="SSIAP 1" info={candidate.diplomas?.SSIAP1} />
              <DiplomaBadge label="SSIAP 2" info={candidate.diplomas?.SSIAP2} />
              <DiplomaBadge label="SSIAP 3" info={candidate.diplomas?.SSIAP3} />
              <DiplomaBadge label="SST" info={candidate.diplomas?.SST} />
            </div>
          </div>
          {candidate.previous_applications?.length > 0 && <PreviousApplications list={candidate.previous_applications} />}
          {candidate.message && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Message du candidat</div>
              <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded p-3 whitespace-pre-wrap">{candidate.message}</div>
            </div>
          )}
          <div className="text-[11px] text-slate-500">
            Reçu le {candidate.created_at?.slice(0, 10)} — Statut : <span className={`inline-block px-2 py-0.5 rounded border text-[10px] ${STATUS_LABELS[candidate.status]?.cls}`}>{STATUS_LABELS[candidate.status]?.label || candidate.status}</span>
            {candidate.converted_to_agent_id && <span className="ml-2 text-emerald-700">• Converti en agent</span>}
          </div>
          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{err}</div>}
          {msg && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">{msg}</div>}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-2 flex-wrap">
          <div className="flex gap-1">
            {["new", "reviewed", "rejected"].map((s) => (
              <button key={s} onClick={()=>setStatus(s)} disabled={busy || candidate.status === s} className={`text-[11px] px-2 py-1 rounded border ${candidate.status === s ? "bg-slate-100" : "hover:bg-slate-50"} ${STATUS_LABELS[s].cls}`}>
                {STATUS_LABELS[s].label}
              </button>
            ))}
          </div>
          <div className="flex gap-2 flex-wrap">
            <RgpdExportButton url={`/candidates/${candidate.id}/rgpd-export.pdf`} testid="candidate-rgpd-export" />
            <RgpdEraseButton url={`/candidates/${candidate.id}/rgpd-erase`} who={`${candidate.first_name} ${candidate.last_name}`} onDone={() => { onDeleted?.(); onClose(); }} testid="candidate-rgpd-erase" />
            {candidate.deleted ? <>
              <button data-testid="candidate-delete-forever" onClick={delForever} disabled={busy} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-60"><Trash2 className="h-3.5 w-3.5" /> {delTitle() === "Supprimer" ? "Supprimer définitivement" : "Demander la suppression définitive"}</button>
              <button data-testid="candidate-restore" onClick={restore} disabled={busy} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"><RotateCcw className="h-3.5 w-3.5" /> Restaurer</button>
            </> : <>
            <button data-testid="candidate-delete" onClick={del} disabled={busy} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-60">
              <Trash2 className="h-3.5 w-3.5" /> {delTitle()}
            </button>
            {(candidate.converted_to_agent_id || candidate.status === "hired") ? (
              <button data-testid="candidate-cancel-hire" onClick={cancelHire} disabled={busy} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-60">
                <Undo2 className="h-3.5 w-3.5" /> Annuler le recrutement
              </button>
            ) : (
            <button data-testid="candidate-convert" onClick={convert} disabled={busy || candidate.converted_to_agent_id || !!candidate.blacklist_match} title={candidate.blacklist_match ? "Recrutement interdit (blacklist)" : ""} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md text-white disabled:opacity-60" style={{ backgroundColor: "var(--brand-primary)" }}>
              <UserCheck className="h-3.5 w-3.5" /> Convertir en agent
            </button>
            )}
            </>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Candidates() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("active");
  const [sort, setSort] = useState("recent");  // recent | postal | city | name
  const [selected, setSelected] = useState(null);
  const [err, setErr] = useState("");
  const { lastEvent } = useRealtime();

  const [blocked, setBlocked] = useState([]);
  const load = async () => {
    try {
      const [r, b] = await Promise.all([http.get("/candidates"), http.get("/candidates/blocked-attempts")]);
      setItems(r.data); setBlocked(b.data);
    }
    catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (lastEvent?.event === "candidate_changed") load(); }, [lastEvent]);

  const inTab = items.filter((i) => tabOf(i) === tab);
  const filtered = tab !== "active" || filter === "all" ? inTab : inTab.filter((i) => i.status === filter);
  const shown = [...filtered].sort((a, b) => {
    if (sort === "age_asc" || sort === "age_desc") {
      const x = ageOf(a.birth_date), y = ageOf(b.birth_date);
      if (x == null) return 1;
      if (y == null) return -1;
      return sort === "age_asc" ? x - y : y - x;
    }
    if (sort === "postal") return (a.postal_code || "").localeCompare(b.postal_code || "", "fr", { numeric: true });
    if (sort === "city") return (a.city || "").localeCompare(b.city || "", "fr", { sensitivity: "base" });
    if (sort === "name") return (a.last_name || "").localeCompare(b.last_name || "", "fr", { sensitivity: "base" });
    // recent: created_at descending
    return (b.created_at || "").localeCompare(a.created_at || "");
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-extrabold">Candidatures</h1>
          <p className="text-sm text-slate-600">{items.filter((i) => tabOf(i) === "active").length} candidature(s) en cours — reçues via la page publique.</p>
        </div>
        <div className="text-xs text-slate-500 bg-slate-100 rounded-md px-3 py-2 font-mono">
          Lien public : {window.location.origin}/postuler
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200" data-testid="cand-tabs">
        {TABS.map(([k, l, Icon, cls]) => (
          <button key={k} data-testid={`cand-tab-${k}`} onClick={() => { setTab(k); setFilter("all"); }}
            className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 inline-flex items-center gap-1.5 transition-colors ${tab === k ? cls : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon className="h-3.5 w-3.5" />{l}<span className="text-[11px] rounded-full bg-slate-100 px-1.5 py-0.5 text-slate-600">{items.filter((i) => tabOf(i) === k).length}</span>
          </button>
        ))}
        <button data-testid="cand-tab-blocked" onClick={() => setTab("blocked")}
          className={`px-4 py-2 text-sm font-semibold -mb-px border-b-2 inline-flex items-center gap-1.5 transition-colors ${tab === "blocked" ? "border-rose-600 text-rose-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
          <ShieldAlert className="h-3.5 w-3.5" />Tentatives bloquées<span className={`text-[11px] rounded-full px-1.5 py-0.5 ${blocked.length ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600"}`}>{blocked.length}</span>
        </button>
      </div>

      {tab === "blocked" ? <BlockedAttempts items={blocked} onChanged={load} /> : <>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1 flex-wrap">
          {tab === "active" && ["all", "new", "reviewed", "rejected"].map((s) => (
            <button
              key={s}
              data-testid={`cand-filter-${s}`}
              onClick={()=>setFilter(s)}
              className={`text-xs font-semibold px-3 py-1.5 rounded border ${filter === s ? "text-white" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
              style={filter === s ? { backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" } : {}}
            >
              {s === "all" ? `Tous (${inTab.length})` : `${STATUS_LABELS[s].label} (${inTab.filter((i)=>i.status===s).length})`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-xs">
          <label className="text-slate-500 font-medium">Trier par :</label>
          <select data-testid="cand-sort" value={sort} onChange={(e)=>setSort(e.target.value)}
                  className="border border-slate-300 rounded px-2 py-1.5 bg-white text-slate-700 text-xs">
            <option value="recent">Date (plus récent)</option>
            <option value="postal">Code postal (croissant)</option>
            <option value="city">Ville (A→Z)</option>
            <option value="name">Nom (A→Z)</option>
            <option value="age_asc">Âge (plus jeune)</option>
            <option value="age_desc">Âge (plus âgé)</option>
          </select>
        </div>
      </div>

      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="py-2 px-3 text-left">Candidat</th>
              <th className="py-2 px-3 text-left">Contact</th>
              <th className="py-2 px-3 text-left">Carte pro</th>
              <th className="py-2 px-3 text-left">Diplômes</th>
              <th className="py-2 px-3 text-left">Statut</th>
              <th className="py-2 px-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {shown.length === 0 ? (
              <tr><td colSpan={6} className="py-6 text-center text-slate-500 text-sm">
                {tab === "active" ? <>Aucune candidature{filter !== "all" ? ` (${STATUS_LABELS[filter]?.label.toLowerCase()})` : ""}. Partage le lien <b>{window.location.origin}/postuler</b> pour commencer à en recevoir.</> : `Aucun candidat dans « ${TABS.find((t) => t[0] === tab)[1]} ».`}
              </td></tr>
            ) : shown.map((c) => (
              <tr key={c.id} data-testid={`cand-row-${c.id}`} className={`hover:bg-slate-50 ${c.blacklist_match ? "bg-rose-50/60" : ""}`}>
                <td className="py-2 px-3">
                  <div className="flex items-center gap-2.5">
                    <CandidatePhoto fid={c.photo_file_id} className="h-11 w-9" testid={`cand-photo-${c.id}`} />
                    <div>
                      <div className={`font-medium flex items-center gap-1.5 ${c.blacklist_match ? "text-rose-700" : ""}`} data-testid={c.blacklist_match ? `cand-blacklisted-${c.id}` : undefined}>{c.civility} {c.first_name} {c.last_name} <BlacklistBadge m={c.blacklist_match} />{c.cv_file_id && <FileText className="h-3.5 w-3.5 text-sky-600" title="CV joint" data-testid={`cand-has-cv-${c.id}`} />}</div>
                      <div className="text-[11px] text-slate-500">{c.created_at?.slice(0, 10)}{ageOf(c.birth_date) != null && <> · <b className="text-slate-700" data-testid={`cand-age-${c.id}`}>{ageOf(c.birth_date)} ans</b></>}{c.deleted && c.deleted_at && <> · supprimé le {c.deleted_at.slice(0, 10)}</>}</div>
                    </div>
                  </div>
                </td>
                <td className="py-2 px-3 text-xs">
                  <div className="text-sky-700">{c.email}</div>
                  <div className="text-slate-600">{c.phone}</div>
                  <div className="text-slate-500 text-[11px] mt-0.5">
                    <span className="font-mono">{c.postal_code || "—"}</span> {c.city || ""}
                  </div>
                </td>
                <td className="py-2 px-3 text-xs font-mono">{c.carte_pro_number}</td>
                <td className="py-2 px-3">
                  <div className="flex flex-wrap gap-1">
                    {["SSIAP1", "SSIAP2", "SSIAP3", "SST"].filter((code) => c.diplomas?.[code]?.has).map((code) => (
                      <span key={code} className="text-[9px] px-1 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">{code}</span>
                    ))}
                    {!["SSIAP1", "SSIAP2", "SSIAP3", "SST"].some((code) => c.diplomas?.[code]?.has) && <span className="text-[10px] text-slate-400">Aucun</span>}
                  </div>
                </td>
                <td className="py-2 px-3">
                  <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_LABELS[c.status]?.cls}`}>{STATUS_LABELS[c.status]?.label}</span>
                  {c.previous_applications?.length > 0 && <span data-testid={`cand-reapplied-${c.id}`} title={`${c.previous_applications.length} candidature(s) précédente(s)`} className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200 font-semibold">{c.previous_applications.length + 1}ᵉ candidature</span>}
                </td>
                <td className="py-2 px-3 text-right">
                  <button data-testid={`cand-view-${c.id}`} onClick={()=>setSelected(c)} className="p-1.5 rounded hover:bg-slate-100"><Eye className="h-4 w-4 text-slate-600" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </>}

      {selected && <CandidateDialog candidate={selected} onClose={()=>setSelected(null)} onConverted={()=>{ load(); setSelected(null); }} onDeleted={load} />}
    </div>
  );
}

const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function PreviousApplications({ list }) {
  const [open, setOpen] = useState(null);
  return (
    <div data-testid="candidate-previous-apps">
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1"><History className="h-3 w-3" /> Candidatures précédentes ({list.length})</div>
      <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
        {list.slice().reverse().map((p, i) => (
          <div key={i}>
            <button type="button" onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-slate-50" data-testid={`prev-app-${i}`}>
              <span>Reçue le <b>{fmtD(p.created_at)}</b></span>
              <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_LABELS[p.status]?.cls || ""}`}>{STATUS_LABELS[p.status]?.label || p.status}{p.deleted ? " · supprimée" : ""}</span>
            </button>
            {open === i && (
              <div className="px-3 pb-3 text-xs text-slate-700 grid grid-cols-2 gap-x-4 gap-y-1">
                <div>Email : {p.email}</div><div>Téléphone : {p.phone}</div>
                <div className="col-span-2">Adresse : {[p.address, p.postal_code, p.city].filter(Boolean).join(" ")}</div>
                <div className="col-span-2">Carte pro : <span className="font-mono">{p.carte_pro_number}</span></div>
                {p.message && <div className="col-span-2 whitespace-pre-wrap bg-slate-50 rounded p-2 mt-1">{p.message}</div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function BlockedAttempts({ items, onChanged }) {
  const del = async (b) => { if (window.confirm("Effacer cette note ?")) { await http.delete(`/candidates/blocked-attempts/${b.id}`); onChanged(); } };
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="blocked-attempts">
      <div className="px-4 py-2.5 bg-rose-50 border-b border-rose-200 text-xs text-rose-900">Personnes blacklistées ou anciens agents supprimés ayant tenté de postuler. Leur candidature a été refusée avec le message « Vous ne pouvez plus postuler auprès de notre société. »</div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
          <tr><th className="text-left py-2 px-3">Date</th><th className="text-left py-2 px-3">Personne</th><th className="text-left py-2 px-3">Contact</th><th className="text-left py-2 px-3">Raison</th><th className="text-left py-2 px-3">Correspondance</th><th></th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.map((b) => (
            <tr key={b.id} data-testid={`blocked-${b.id}`}>
              <td className="py-2 px-3 text-xs whitespace-nowrap">{new Date(b.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
              <td className="py-2 px-3 font-medium">{b.civility} {b.first_name} {b.last_name}{ageOf(b.birth_date) != null && <span className="text-[11px] text-slate-500"> · {ageOf(b.birth_date)} ans</span>}</td>
              <td className="py-2 px-3 text-xs">{b.email}<br />{b.phone}</td>
              <td className="py-2 px-3"><span className={`text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${b.kind === "blacklisted" ? "bg-rose-600 text-white" : "bg-slate-700 text-white"}`}>{b.kind === "blacklisted" ? "Blacklisté" : "Agent supprimé"}</span></td>
              <td className="py-2 px-3 text-xs">{b.match?.name} <span className="text-slate-500">({(b.match?.matched_on || []).join(", ")})</span>{b.match?.reason && <div className="text-slate-500">{b.match.reason}</div>}</td>
              <td className="py-2 px-3 text-right"><button onClick={() => del(b)} className="p-1.5 rounded hover:bg-rose-50" title="Effacer"><Trash2 className="h-4 w-4 text-rose-600" /></button></td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-10 text-sm">Aucune tentative bloquée.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
